import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  customersService,
  financialCyclesService,
  expendituresService,
  expenditureCategoriesService,
  mortalitiesService,
  feedService,
  requestsService,
} from '../services/apiServices';
import { queryKeys } from '../config/queryClient';
import {
  Customer,
  FinancialCycle,
  Expenditure,
  ExpenditureCategory,
  ExpenditureGroup,
  Mortality,
  Request,
  PaginatedResponse,
  Stats,
  MortalityStats,
  CustomerStats,
} from '../types';

// ============== Customers ==============

export const useCustomers = (params?: any) => {
  return useQuery<PaginatedResponse<Customer>>({
    queryKey: queryKeys.customersList(params),
    queryFn: () => customersService.getAll(params),
  });
};

export const useCustomer = (id: number, enabled = true) => {
  return useQuery<Customer>({
    queryKey: queryKeys.customer(id),
    queryFn: () => customersService.getById(id),
    enabled,
  });
};

export const useCustomersStats = () => {
  return useQuery<CustomerStats>({
    queryKey: queryKeys.customersStats,
    queryFn: customersService.getStats,
  });
};

export const useCustomerSearch = (query: string, enabled = true) => {
  return useQuery<Customer[]>({
    queryKey: ['customers', 'search', query],
    queryFn: () => customersService.search(query),
    enabled: enabled && query.length > 0,
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Customer>) => customersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers });
      queryClient.invalidateQueries({ queryKey: queryKeys.customersStats });
    },
  });
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Customer> }) =>
      customersService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customer(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers });
    },
  });
};

export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers });
      queryClient.invalidateQueries({ queryKey: queryKeys.customersStats });
    },
  });
};

// ============== Financial Cycles ==============

export const useFinancialCycles = () => {
  return useQuery<FinancialCycle[]>({
    queryKey: queryKeys.financialCycles,
    queryFn: financialCyclesService.getAll,
  });
};

export const useCurrentFinancialCycle = () => {
  return useQuery<FinancialCycle>({
    queryKey: queryKeys.financialCyclesCurrent,
    queryFn: financialCyclesService.getCurrent,
  });
};

export const useFinancialCycleSummary = () => {
  return useQuery({
    queryKey: queryKeys.financialCyclesSummary,
    queryFn: financialCyclesService.getSummary,
  });
};

export const useFinancialCycleTrends = () => {
  return useQuery({
    queryKey: queryKeys.financialCyclesTrends,
    queryFn: financialCyclesService.getTrends,
  });
};

export const useCloseFinancialCycle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => financialCyclesService.close(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.financialCycles });
      queryClient.invalidateQueries({ queryKey: queryKeys.financialCyclesCurrent });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// ============== Expenditures ==============

export const useExpenditures = (params?: any) => {
  return useQuery<PaginatedResponse<ExpenditureGroup>>({
    queryKey: queryKeys.expendituresList(params),
    queryFn: () => expendituresService.getAll(params),
  });
};

export const useExpenditure = (id: number, enabled = true) => {
  return useQuery<Expenditure>({
    queryKey: queryKeys.expenditure(id),
    queryFn: () => expendituresService.getById(id),
    enabled,
  });
};

export const useExpendituresStats = () => {
  return useQuery<Stats>({
    queryKey: queryKeys.expendituresStats,
    queryFn: expendituresService.getStats,
  });
};

export const usePendingExpenditures = () => {
  return useQuery<Expenditure[]>({
    queryKey: queryKeys.expendituresPending,
    queryFn: expendituresService.getPending,
  });
};

export const useCreateExpenditure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Expenditure>) => expendituresService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditures });
      queryClient.invalidateQueries({ queryKey: queryKeys.expendituresStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

export const useUpdateExpenditure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Expenditure> }) =>
      expendituresService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditure(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditures });
    },
  });
};

export const useDeleteExpenditure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => expendituresService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditures });
      queryClient.invalidateQueries({ queryKey: queryKeys.expendituresStats });
    },
  });
};

export const useMakeExpenditurePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      expendituresService.makePayment(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditure(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditures });
      queryClient.invalidateQueries({ queryKey: queryKeys.expendituresPending });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

// ============== Expenditure Categories ==============

export const useExpenditureCategories = () => {
  return useQuery<ExpenditureCategory[]>({
    queryKey: queryKeys.expenditureCategories,
    queryFn: expenditureCategoriesService.getAll,
  });
};

export const useCreateExpenditureCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ExpenditureCategory>) => expenditureCategoriesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenditureCategories });
    },
  });
};

// ============== Mortalities ==============

export const useMortalities = (params?: any) => {
  return useQuery<PaginatedResponse<Mortality>>({
    queryKey: queryKeys.mortalitiesList(params),
    queryFn: () => mortalitiesService.getAll(params),
  });
};

export const useMortalitiesStats = (params?: any) => {
  return useQuery<MortalityStats>({
    queryKey: queryKeys.mortalitiesStats(),
    queryFn: () => mortalitiesService.getStats(params),
  });
};

export const useMortalitiesTrends = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.mortalitiesTrends(params),
    queryFn: () => mortalitiesService.getTrends(params),
  });
};

export const useCreateMortality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Mortality>) => mortalitiesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mortalities });
      queryClient.invalidateQueries({ queryKey: queryKeys.mortalitiesStats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.birds });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardMetrics });
    },
  });
};

// ============== Feed ==============

export const useFeedDashboard = () => {
  return useQuery({
    queryKey: queryKeys.feedDashboard,
    queryFn: feedService.getDashboard,
  });
};

export const useFeedTypes = () => {
  return useQuery({
    queryKey: queryKeys.feedTypes,
    queryFn: feedService.getTypes,
  });
};

export const useFeedBrands = () => {
  return useQuery({
    queryKey: queryKeys.feedBrands,
    queryFn: feedService.getBrands,
  });
};

export const useFeedStock = () => {
  return useQuery({
    queryKey: queryKeys.feedStock,
    queryFn: feedService.getStock,
  });
};

export const useFeedStockAlerts = () => {
  return useQuery({
    queryKey: queryKeys.feedStockAlerts,
    queryFn: feedService.getStockAlerts,
  });
};

export const useFeedProcurements = () => {
  return useQuery({
    queryKey: queryKeys.feedProcurements,
    queryFn: feedService.getProcurements,
  });
};

export const useProcureFeed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => feedService.procureFeed(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.feedProcurements });
      queryClient.invalidateQueries({ queryKey: queryKeys.feedStock });
      queryClient.invalidateQueries({ queryKey: queryKeys.feedDashboard });
    },
  });
};

// ============== Requests ==============

export const useRequests = (params?: any) => {
  return useQuery<PaginatedResponse<Request>>({
    queryKey: queryKeys.requestsList(params),
    queryFn: () => requestsService.getAll(params),
  });
};

export const useRequest = (id: number, enabled = true) => {
  return useQuery<Request>({
    queryKey: queryKeys.request(id),
    queryFn: () => requestsService.getById(id),
    enabled,
  });
};

export const useRequestsStats = () => {
  return useQuery<Stats>({
    queryKey: queryKeys.requestsStats,
    queryFn: requestsService.getStats,
  });
};

export const useCreateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Request>) => requestsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.requests });
      queryClient.invalidateQueries({ queryKey: queryKeys.requestsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};

export const useAssignRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) =>
      requestsService.assign(id, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.request(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.requests });
    },
  });
};

export const useCompleteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => requestsService.complete(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.request(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.requests });
      queryClient.invalidateQueries({ queryKey: queryKeys.requestsStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardOverview });
    },
  });
};
